import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { vitalSignsService } from '../services/vitalSignsService'
import { VitalSigns } from '../types/vitalSigns'
import { formatTemperature, formatHeight, formatWeight } from '../utils/vitalSignsUtils'
import { UnitPreference } from '../types/vitalSigns'
import { formatDate } from '../utils/locale'
import { MaskedField } from './MaskedField'
import './VitalSignsList.css'

interface VitalSignsListProps {
  patientId: string
  consultationId?: string
  userPreference?: UnitPreference
  onVitalSignsClick?: (vitalSigns: VitalSigns) => void
}

type SortField = 'recordedAt' | 'bloodPressure' | 'heartRate' | 'temperature'
type SortDirection = 'asc' | 'desc'

export function VitalSignsList({
  patientId,
  consultationId,
  userPreference = 'metric',
  onVitalSignsClick,
}: VitalSignsListProps) {
  const [sortField, setSortField] = useState<SortField>('recordedAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const { data: vitalSigns, isLoading } = useQuery({
    queryKey: consultationId 
      ? ['vital-signs', 'consultation', consultationId]
      : ['vital-signs', 'patient', patientId],
    queryFn: () => {
      if (consultationId) {
        return vitalSignsService.getByConsultation(consultationId)
      }
      return vitalSignsService.getByPatient(patientId)
    },
    enabled: !!patientId,
  })

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  if (isLoading) {
    return <div className="vital-signs-list loading">Loading vital signs...</div>
  }

  if (!vitalSigns || vitalSigns.length === 0) {
    return <div className="vital-signs-list empty">No vital signs recorded</div>
  }

  return (
    <MaskedField
      field="vital-signs"
      value={vitalSigns}
      patientId={patientId}
      confidentialityLevel="restricted"
    >
      {(canAccess, maskedVitalSigns, requestEmergencyAccess) => {
        if (!canAccess) {
          return (
            <div className="vital-signs-list empty">
              <p>You do not have permission to view vital signs.</p>
              {requestEmergencyAccess && (
                <button
                  onClick={requestEmergencyAccess}
                  className="btn-secondary btn-small"
                  style={{ marginTop: '1rem' }}
                >
                  Request Emergency Access
                </button>
              )}
            </div>
          )
        }

        if (!maskedVitalSigns || maskedVitalSigns.length === 0) {
          return <div className="vital-signs-list empty">No vital signs recorded</div>
        }

        const sortedVitalSigns = [...(maskedVitalSigns || [])].sort((a, b) => {
          let aValue: any
          let bValue: any

          switch (sortField) {
            case 'recordedAt':
              aValue = new Date(a.recordedAt || a.createdAt).getTime()
              bValue = new Date(b.recordedAt || b.createdAt).getTime()
              break
            case 'bloodPressure':
              aValue = a.bloodPressure?.systolic || 0
              bValue = b.bloodPressure?.systolic || 0
              break
            case 'heartRate':
              aValue = a.heartRate?.value || 0
              bValue = b.heartRate?.value || 0
              break
            case 'temperature':
              aValue = a.temperature?.value || 0
              bValue = b.temperature?.value || 0
              break
            default:
              return 0
          }

          if (sortDirection === 'asc') {
            return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
          } else {
            return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
          }
        })

        return (
          <div className="vital-signs-list">
      <div className="vital-signs-table-container">
        <table className="vital-signs-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('recordedAt')} className="sortable">
                Date {sortField === 'recordedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('bloodPressure')} className="sortable">
                BP {sortField === 'bloodPressure' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('heartRate')} className="sortable">
                HR {sortField === 'heartRate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('temperature')} className="sortable">
                Temp {sortField === 'temperature' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th>RR</th>
              <th>Weight</th>
              <th>Height</th>
              <th>BMI</th>
              <th>Recorded By</th>
            </tr>
          </thead>
          <tbody>
            {sortedVitalSigns.map((vs) => (
              <tr
                key={vs.id}
                onClick={() => onVitalSignsClick?.(vs)}
                className={onVitalSignsClick ? 'clickable' : ''}
              >
                <td>{formatDate(vs.recordedAt || vs.createdAt)}</td>
                <td>
                  {vs.bloodPressure
                    ? `${vs.bloodPressure.systolic}/${vs.bloodPressure.diastolic}`
                    : '-'}
                </td>
                <td>{vs.heartRate ? `${vs.heartRate.value}` : '-'}</td>
                <td>
                  {vs.temperature
                    ? formatTemperature(
                        vs.temperature.value,
                        vs.temperature.unit === 'celsius' ? 'celsius' : 'fahrenheit',
                      )
                    : '-'}
                </td>
                <td>{vs.respiratoryRate ? `${vs.respiratoryRate.value}` : '-'}</td>
                <td>
                  {vs.weight
                    ? formatWeight(
                        vs.weight.value,
                        vs.weight.unit as 'kg' | 'lb' | 'stone',
                        userPreference,
                      )
                    : '-'}
                </td>
                <td>
                  {vs.height
                    ? formatHeight(
                        vs.height.value,
                        vs.height.unit as 'cm' | 'in',
                        userPreference,
                      )
                    : '-'}
                </td>
                <td>{vs.bmi ? vs.bmi.toFixed(1) : '-'}</td>
                <td>{vs.recordedByName || vs.recordedBy || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
        )
      }}
    </MaskedField>
  )
}
